import { useEffect, useState } from 'react'

const PHASE_MESSAGES = {
  scraping: [
    { icon: '🚀', text: 'Démarrage du scraping...' },
    { icon: '🔗', text: 'Connexion aux portails immobiliers...' },
  ],
  polling: [
    { icon: '🏠', text: 'Exploration des annonces en ligne...' },
    { icon: '🌐', text: 'Parcours des sites LeBonCoin, PAP...' },
    { icon: '🔍', text: 'Filtrage par localisation...' },
    { icon: '💶', text: 'Vérification des fourchettes de prix...' },
    { icon: '📐', text: 'Analyse des surfaces et configurations...' },
    { icon: '📅', text: 'Tri par date de publication...' },
    { icon: '📊', text: 'Consolidation des résultats...' },
  ],
  fetching: [
    { icon: '⬇️', text: 'Récupération des annonces...' },
    { icon: '✅', text: 'Finalisation...' },
  ],
}

const MAX_POLL_EXPECTED = 90

function formatElapsed(seconds) {
  if (seconds <= 0) return null
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}min ${seconds % 60}s`
}

export function ImmobilierSearchingAnimation({ status, filters, pollCount, onCancel }) {
  const [msgIndex, setMsgIndex] = useState(0)
  const [dots, setDots] = useState('')

  const messages = PHASE_MESSAGES[status] ?? PHASE_MESSAGES.polling

  useEffect(() => {
    setMsgIndex(0)
  }, [status])

  useEffect(() => {
    const msgTimer = setInterval(() => setMsgIndex((i) => (i + 1) % messages.length), 2800)
    const dotsTimer = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 500)
    return () => { clearInterval(msgTimer); clearInterval(dotsTimer) }
  }, [messages.length])

  const current = messages[msgIndex] ?? messages[0]

  const summary = [
    filters?.transactionType,
    filters?.propertyType,
    (filters?.locationLabel || filters?.location) && `à ${filters.locationLabel ?? filters.location}`,
    filters?.priceMax && `max ${Number(filters.priceMax).toLocaleString()}€`,
  ].filter(Boolean).join(' · ')

  const progressPct = status === 'scraping' ? 3
    : status === 'fetching' ? 97
    : Math.min(5 + (pollCount / MAX_POLL_EXPECTED) * 88, 93)

  const elapsedSeconds = (status === 'polling' || status === 'fetching') ? pollCount * 15 : 0
  const elapsedStr = formatElapsed(elapsedSeconds)

  const phaseLabel = {
    scraping: 'Initialisation',
    polling: 'Scraping en cours',
    fetching: 'Chargement',
  }[status] ?? ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 24px 32px', gap: 16 }}>
      {/* Barre de progression */}
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: 'rgba(91, 92, 244,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
            {phaseLabel}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {elapsedStr && (
              <span style={{ fontSize: 11, color: 'rgb(var(--rgb-ink) / 0.25)' }}>⏱ {elapsedStr}</span>
            )}
            <span style={{ fontSize: 11, color: 'rgba(91, 92, 244,0.5)', fontWeight: 600 }}>{Math.round(progressPct)}%</span>
          </div>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(91, 92, 244,0.1)', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, #3a3bb0, #5b5cf4)', width: `${progressPct}%`, transition: 'width 1.5s ease' }} />
        </div>
      </div>

      {/* Spinner */}
      <div style={{ position: 'relative', width: 80, height: 80, marginTop: 8 }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid rgba(91, 92, 244,0.1)', borderTop: '3px solid #5b5cf4', animation: 'spin 1s linear infinite' }} />
        <div style={{ position: 'absolute', inset: 12, borderRadius: '50%', border: '2px solid rgba(91, 92, 244,0.05)', borderBottom: '2px solid rgba(91, 92, 244,0.35)', animation: 'spin 1.8s linear infinite reverse' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
          {current.icon}
        </div>
      </div>

      {/* Query summary */}
      {summary && (
        <div style={{ fontSize: 12, color: 'rgb(var(--rgb-ink) / 0.3)', fontStyle: 'italic', maxWidth: 400, textAlign: 'center' }}>
          « {summary} »
        </div>
      )}

      {/* Dynamic message */}
      <div style={{ fontSize: 14, color: 'rgb(var(--rgb-ink) / 0.65)', minHeight: 22, textAlign: 'center' }}>
        {current.text}{dots}
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 6 }}>
        {messages.map((_, i) => (
          <div key={i} style={{ width: i === msgIndex ? 20 : 6, height: 6, borderRadius: 3, background: i === msgIndex ? '#5b5cf4' : 'rgb(var(--rgb-ink) / 0.1)', transition: 'all 0.3s ease' }} />
        ))}
      </div>

      <div style={{ fontSize: 11, color: 'rgb(var(--rgb-ink) / 0.2)' }}>
        Le scraping des portails immobiliers prend 10 à 40 minutes selon le nombre de pages...
      </div>

      {onCancel && (
        <button
          onClick={onCancel}
          style={{
            marginTop: 4,
            padding: '7px 22px',
            borderRadius: 8,
            background: 'rgb(var(--rgb-ink) / 0.04)',
            border: '1px solid rgb(var(--rgb-ink) / 0.12)',
            color: 'rgb(var(--rgb-ink) / 0.45)',
            fontSize: 13,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,60,60,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,60,60,0.3)'; e.currentTarget.style.color = 'rgba(255,120,120,0.9)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.04)'; e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.12)'; e.currentTarget.style.color = 'rgb(var(--rgb-ink) / 0.45)' }}
        >
          Annuler le scraping
        </button>
      )}
    </div>
  )
}

export default function ImmobilierLoadingAnimation({ targetNumber, filters, onComplete }) {
  const [counter, setCounter] = useState(0)
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('Récupération des annonces...')

  const messages = ['Récupération des annonces...', 'Calcul prix au m²...', 'Tri par date...', 'Finalisation...']

  useEffect(() => {
    const duration = Math.min(2000 + (targetNumber / 50) * 200, 12000)
    const steps = 100
    const counterIncrement = targetNumber / steps
    const intervalMs = duration / steps
    let currentCounter = 0
    let currentProgress = 0

    const timer = setInterval(() => {
      currentCounter += counterIncrement
      currentProgress += 1
      if (currentCounter >= targetNumber) currentCounter = targetNumber
      setCounter(Math.floor(currentCounter))
      setProgress(currentProgress)
      const idx = Math.floor(currentProgress / (100 / messages.length))
      if (idx < messages.length) setMessage(messages[idx])
      if (currentProgress >= 100) {
        clearInterval(timer)
        setMessage('✓ Annonces chargées !')
        setTimeout(() => onComplete?.(), 300)
      }
    }, intervalMs)

    return () => clearInterval(timer)
  }, [targetNumber, onComplete])

  const summary = [filters?.transactionType, filters?.propertyType, (filters?.locationLabel || filters?.location) && `à ${filters.locationLabel ?? filters.location}`].filter(Boolean).join(' · ')

  return (
    <div className="preview-inline-container">
      <div className="preview-counter-section">
        <div className="preview-counter-label" style={{ color: 'rgba(91, 92, 244,0.7)' }}>Annonces trouvées</div>
        <div className="preview-counter-number" style={{ color: '#5b5cf4', transform: counter === targetNumber ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.2s' }}>
          {counter.toLocaleString()}
        </div>
        {summary && <div className="preview-query">"{summary}"</div>}
        <div className="preview-loading-bar" style={{ background: 'rgba(91, 92, 244,0.1)' }}>
          <div className="preview-loading-fill" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #3a3bb0, #5b5cf4)' }} />
        </div>
        <div className="preview-loading-text" style={{ color: progress === 100 ? '#5b5cf4' : 'inherit' }}>{message}</div>
      </div>
    </div>
  )
}
